var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __read = (this && this.__read) || function (o, n) {
    var m = typeof Symbol === "function" && o[Symbol.iterator];
    if (!m) return o;
    var i = m.call(o), r, ar = [], e;
    try {
        while ((n === void 0 || n-- > 0) && !(r = i.next()).done) ar.push(r.value);
    }
    catch (error) { e = { error: error }; }
    finally {
        try {
            if (r && !r.done && (m = i["return"])) m.call(i);
        }
        finally { if (e) throw e.error; }
    }
    return ar;
};
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
//---------------------------------------------------------------------------------------------------
// ## IMPORTS ##
// Library Imports
import { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from "react";
// Material ui Imports
import { styled } from "@mui/system";
import { Box, Card, CardActionArea, CardContent, CardMedia, Grid, IconButton, Skeleton, Tooltip, Typography, } from "@mui/material";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import { useOnWindowResize } from "../hooks/useOnWindowResize";
import VTTextField from "./VTTextField";
import VTMenu from "./VTMenu";
import textCutOffAddElipsis from "../utils/textCutOffAddElipsis";
import SaveAsRoundedIcon from "@mui/icons-material/SaveAsRounded";
var StyledCard = styled(Card)(function () { return ({
    borderRadius: 15,
    maxWidth: "400px",
}); });
export var OptionSelected;
(function (OptionSelected) {
    OptionSelected[OptionSelected["START_LOCATION"] = 0] = "START_LOCATION";
    OptionSelected[OptionSelected["DOWNLOAD"] = 1] = "DOWNLOAD";
    OptionSelected[OptionSelected["COPY"] = 2] = "COPY";
    OptionSelected[OptionSelected["DELETE"] = 3] = "DELETE";
})(OptionSelected || (OptionSelected = {}));
export default forwardRef(function (props, ref) {
    var sx = props.sx;
    var rootRef = useRef(null);
    var vtMenuExposedMethodsRef = useRef(null);
    useImperativeHandle(ref, function () { return ({
        getRootNode: function () {
            return rootRef.current;
        },
        closePopUpOptionsMenu: function () {
            var _a;
            (_a = vtMenuExposedMethodsRef.current) === null || _a === void 0 ? void 0 : _a.closePopUpMenu();
        },
    }); });
    return (_jsx(StyledCard, __assign({ ref: rootRef, elevation: 5, sx: sx }, { children: _jsx(CardContentComponentWrapper, __assign({ ref: vtMenuExposedMethodsRef }, props)) })));
});
// Card content for the visuals and layout of the VTCard
var CardContentComponentWrapper = forwardRef(function (_a, ref) {
    var title = _a.title, text = _a.text, scrollTextTipOn = _a.scrollTextTipOn, sx = _a.sx, src = _a.src, menuOptions = _a.menuOptions, loading = _a.loading, _b = _a.titleCutOffPoint, titleCutOffPoint = _b === void 0 ? 80 : _b, _c = _a.textCutOffPoint, textCutOffPoint = _c === void 0 ? 80 : _c, onTitleEdited = _a.onTitleEdited;
    var _d = __read(useOnWindowResize(), 2), windowWidth = _d[0], windowHeight = _d[1];
    var _e = __read(useState(null), 2), cardMediaNode = _e[0], setCardMediaNode = _e[1];
    var CardMediaRef = useCallback(function (node) {
        setCardMediaNode(node);
    }, []);
    var _f = __read(useState(150), 2), cardMediaHeight = _f[0], setCardMediaHeight = _f[1];
    useEffect(function () {
        if (cardMediaNode) {
            var width = cardMediaNode.clientWidth;
            if (185 > width) {
                setCardMediaHeight(150);
            }
            else if (250 > width) {
                setCardMediaHeight(200);
            }
            else if (350 > width) {
                setCardMediaHeight(240);
            }
            else {
                setCardMediaHeight(280);
            }
        }
    }, [cardMediaNode, sx, windowWidth]);
    var _g = __read(useState(document.createElement("video")), 2), video = _g[0], setVideo = _g[1];
    var _h = __read(useState(false), 2), titleInEdit = _h[0], setTitleInEdit = _h[1];
    var _j = __read(useState(""), 2), editedTitleValue = _j[0], setEditedTitleValue = _j[1];
    return (_jsxs(_Fragment, { children: [_jsx(CardActionArea, __assign({ disabled: loading }, { children: _jsxs(CardMedia, __assign({ onClick: function () { }, ref: CardMediaRef, sx: {
                        height: "".concat(cardMediaHeight, "px"),
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        position: "relative",
                    } }, { children: [menuOptions && (_jsx(VTMenu, __assign({ ref: ref, sx: {
                                width: "45px",
                                height: "45px",
                                position: "absolute",
                                top: "8px",
                                right: "8px",
                                zIndex: 2,
                                "& .MuiButtonBase-root": {
                                    "&:hover": { background: "rgb(0, 0, 0, 0.1)" },
                                },
                            }, type: "icon", color: "secondary", popOutMenuZIndex: menuOptions.menuPopOutZIndex, menuOptions: menuOptions.menuOptionsList, direction: "DOWN" }, { children: _jsx(MoreHorizIcon, { sx: { color: "white" } }) }))), !loading ? (_jsx("video", { controls: true, src: src, style: {
                                objectFit: "cover",
                                width: "100%",
                                height: "100%",
                            } })) : (_jsx(Skeleton, { variant: "rectangular", sx: {
                                // objectFit: "cover",
                                borderRadius: "0px",
                                width: "100%",
                                height: "100%",
                            } }))] })) })), _jsx(CardContent, __assign({ sx: __assign({ ":last-child": { paddingBottom: "16px" } }, (titleInEdit && { paddingTop: "2px !important", paddingBottom: "2px !important" })) }, { children: _jsxs(Grid, __assign({ container: true }, { children: [_jsx(Grid, __assign({ item: true, xs: 12, container: true, justifyContent: "center" }, { children: !loading ? (!titleInEdit ? (_jsx(Tooltip, __assign({ disableHoverListener: Boolean(title && title.length < titleCutOffPoint), placement: "top", arrow: true, title: title ? title : "" }, { children: _jsx(Typography
                                // gutterBottom
                                , __assign({ 
                                    // gutterBottom
                                    variant: "h5", fontStyle: {
                                        width: "250px",
                                        textAlign: "center",
                                    }, sx: __assign({}, (onTitleEdited && { cursor: "text" })), onClick: function () {
                                        if (onTitleEdited) {
                                            setEditedTitleValue(title);
                                            setTitleInEdit(true);
                                        }
                                    } }, { children: textCutOffAddElipsis(title, titleCutOffPoint) })) }))) : (_jsxs(Box, __assign({ component: "div", onBlur: function (event) {
                                    // if the blur was because of outside focus
                                    // currentTarget is the parent element, relatedTarget is the clicked element
                                    // this is so clicking the save edit button doesn't lose focus by default
                                    if (!event.currentTarget.contains(event.relatedTarget)) {
                                        window.setTimeout(function () {
                                            setTitleInEdit(false);
                                        }, 100);
                                    }
                                }, sx: {
                                    display: "flex",
                                    alignItems: "center",
                                    width: "100%",
                                } }, { children: [_jsx(VTTextField, { value: editedTitleValue, sx: { flex: 1 }, autoFocus: true, onChange: function (text) {
                                            setEditedTitleValue(text);
                                        }, onKeyDown: function (e) {
                                            var keyCodeForEnterKey = 13;
                                            var keyCodeForEscKey = 27;
                                            if (e.keyCode === keyCodeForEnterKey) {
                                                // grab value
                                                onTitleEdited && onTitleEdited(editedTitleValue);
                                                setTitleInEdit(false);
                                            }
                                            if (e.keyCode === keyCodeForEscKey) {
                                                setTitleInEdit(false);
                                            }
                                        } }), _jsx(IconButton, __assign({ sx: { marginLeft: "10px" }, onClick: function (e) {
                                            onTitleEdited && onTitleEdited(editedTitleValue);
                                            setTitleInEdit(false);
                                        } }, { children: _jsx(SaveAsRoundedIcon, {}) }))] })))) : (_jsx(Skeleton, { variant: "text", sx: { width: "75%", fontSize: "1.4em" } })) })), !loading && (_jsx(Tooltip, __assign({ disableHoverListener: Boolean(text && text.length < textCutOffPoint), placement: "right", arrow: true, title: scrollTextTipOn ? "scroll" : "" }, { children: _jsx(Grid, __assign({ item: true, xs: 12, container: true, justifyContent: "center", sx: {
                                    maxHeight: "60px",
                                    overflowY: "auto",
                                    cursor: "ns-resize",
                                } }, { children: _jsx(Typography, __assign({ align: "center", variant: "body2" }, { children: text && textCutOffAddElipsis(text, textCutOffPoint) })) })) })))] })) }))] }));
});
